import ConfigProvider, { ThemeConfig } from 'antd/lib/config-provider'
import { AliasToken } from 'antd/lib/theme/internal'
import React, { createContext, useContext, useEffect, useState } from 'react'

export type ThemeType = 'light' | 'dark'

const Token: Partial<AliasToken> = {
    borderRadius: 4,
    colorPrimary: '#4B286D',
    colorPrimaryHover: '#331e50',
    controlHeight: 32,
    controlPaddingHorizontal: 12,
    paddingContentHorizontal: 12,
}

const ComponentsCommonConfig: ThemeConfig['components'] = {
    Select: {
        controlHeightLG: 40,
    },
    Table: {
        rowHoverBg: '#F2EFF4',
        rowSelectedBg: '#F2EFF4',
        rowSelectedHoverBg: '#F2EFF4',
        headerSplitColor: 'rgba(0,0,0,.06)',
        headerBg: '#F7F7F8',
    },
    Input: {
        paddingInline: 12,
        activeShadow: '0 0 0 2px #D8CBE5',
        activeBorderColor: '#4b286d',
        hoverBorderColor: '#4b286d',
    },
    InputNumber: {
        paddingInline: 12,
        activeShadow: '0 0 0 2px #D8CBE5',
        activeBorderColor: '#4b286d',
        hoverBorderColor: '#4b286d',
    },
    DatePicker: {
        paddingInline: 12,
        activeShadow: '0 0 0 2px #D8CBE5',
        activeBorderColor: '#4b286d',
        hoverBorderColor: '#4b286d',
    },
    Button: {
        defaultBorderColor: '#E3E6E8',
        defaultBg: '#FFFFFF',
        defaultHoverBg: '#F4F4F7',
    },
    Typography: {
        titleMarginBottom: 0,
        titleMarginTop: 0,
    },
    Pagination: {
        borderRadius: 2,
    },
}

const lightTheme: ThemeConfig = {
    components: {
        Tabs: {
            itemColor: '#676E73',
            itemSelectedColor: '#414547',
            itemHoverColor: '#414547',
            inkBarColor: '#414547',
        },
        ...ComponentsCommonConfig,
    },
    token: Token,
}

const darkTheme: ThemeConfig = {
    components: {
        Tabs: {
            itemSelectedColor: '#ffffff',
        },
        ...ComponentsCommonConfig,
    },
    token: Token,
}

const ThemeContext = createContext<ThemeType | undefined>(undefined)

export const ThemeProvider = ThemeContext.Provider

export const ThemeContextProvider = ({ children }: { children: React.ReactNode }) => {
    const [theme, setTheme] = useState<ThemeType | undefined>('light')
    useEffect(() => {
        const prefersDark = false // window.matchMedia('(prefers-color-scheme: dark)').matches
        if (prefersDark) {
            setTheme('dark')
        } else {
            setTheme('light')
        }
    }, [])
    const themeObject: ThemeConfig = theme === 'dark' ? darkTheme : lightTheme
    return (
        <ThemeContext.Provider value={theme}>
            <ConfigProvider theme={themeObject}>{children}</ConfigProvider>
        </ThemeContext.Provider>
    )
}

export const useTheme = () => {
    const theme = useContext(ThemeContext)
    if (!theme) {
        throw new Error('useTheme must be used within a ThemeProvider')
    }
    return theme
}
